import { generatePath, Link } from "react-router-dom";

import MenuIcon from "@/assets/icons/bars-right.svg";
import ChevronUp from "@/assets/icons/chevron-up.svg";
import CloseIcon from "@/assets/icons/close.svg";
import LogoDark from "@/assets/images/allears-dark.svg";
import LogoLight from "@/assets/images/allears-light.svg";
import { MainNavigation } from "@/containers/MainNavigation";
import PaymentSuccess from "@/containers/Modals/PaymentSuccess";
import { UserSubMenu } from "@/containers/Navigation";
import UpsaleModals from "@/containers/PlanModal/UpsaleModals";
import { useOrganization } from "@/hooks/useOrganization";
import { useToggle } from "@/hooks/useToggle";
import { useUser } from "@/hooks/useUser";
import { INTERCOM_IDS } from "@/intercom";
import { ROUTES } from "@/routes_new";
import { cx } from "@/utils";
import Avatar from "@components/Avatar";
import Clickable from "@components/Clickable";
import ScrollLock from "@components/ScrollLock";

import styles from "./Layout.module.css";

export const Sidebar = ({ open = false, children, className, ...props }) => {
  return (
    <aside
      {...props}
      className={cx(styles.sidebar, className, { [styles.open]: open })}
    >
      <div className={styles.sidebarInner}>{children}</div>
    </aside>
  );
};

const Me = () => {
  const { data: user } = useUser();
  const { name: full_name, last_name, first_name } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { uuid, name } = orgData?.ok ? orgData.data : {};
  if (!uuid) return null;

  return (
    <div className={styles.me}>
      <Link
        to={generatePath(ROUTES.SETTINGS_MY_ACCOUNT_ORGID, { orgUuid: uuid })}
        data-intercom={INTERCOM_IDS.MAIN_MENU_USER}
      >
        <Avatar
          size={40}
          className={styles.avatar}
          first_name={first_name}
          full_name={full_name}
          last_name={last_name}
        />
        <div>
          <span className={styles.name}>{full_name}</span>
          <span className={styles.org} data-testid="org-name">
            {name}
          </span>
        </div>
      </Link>
      <UserSubMenu className={styles.more}>
        <ChevronUp height={18} />
      </UserSubMenu>
    </div>
  );
};

const OpenCloseMenu = ({ open = true, onToggle, className }) => {
  return (
    <Clickable onClick={onToggle} className={className}>
      {open ? (
        <CloseIcon fill="currentColor" width={24} />
      ) : (
        <MenuIcon fill="currentColor" width={24} />
      )}
    </Clickable>
  );
};

const Logo = ({ className, width = 100 }) => {
  return (
    <Clickable href={ROUTES.APP_HOME} className={className}>
      <LogoDark width={width} alt="All Ears Logo" className={styles.logoDark} />
      <LogoLight
        width={width}
        alt="All Ears Logo"
        className={styles.logoLight}
      />
    </Clickable>
  );
};

const TopNav = ({ onToggle, className, ...rest }) => {
  return (
    <div className={cx(styles.top, className)}>
      <Logo />
      <OpenCloseMenu
        onToggle={onToggle}
        className={cx(styles.toggle, styles.mobileOnly)}
        {...rest}
      />
    </div>
  );
};

const Layout = ({ className = undefined, children, mainClassName = "" }) => {
  const [sidebarOpen, toggleSidebarOpen] = useToggle(false);

  return (
    <div className={cx(styles.root, className)}>
      <Sidebar open={sidebarOpen}>
        <TopNav onToggle={toggleSidebarOpen} />
        <MainNavigation />
        <Me />
      </Sidebar>
      <main className={cx(styles.main, mainClassName)}>
        <TopNav
          open={false}
          onToggle={toggleSidebarOpen}
          className={styles.mobileOnly}
        />
        {children}
      </main>
      {sidebarOpen && <ScrollLock />}
      <PaymentSuccess />
      <UpsaleModals />
    </div>
  );
};

export default Layout;
